import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import {
  Project,
  projectsAPI,
  Client,
  clientsAPI,
  Task,
  tasksAPI,
  Specialty,
  specialtiesAPI,
  SuggestedUser,
  User,
  usersAPI,
} from '../../services/api';
import ProjectIntegrations from './ProjectIntegrations';
import {
  PlusIcon,
  SearchIcon,
  FolderIcon,
  CheckIcon,
  EyeIcon,
  EditIcon,
  TrashIcon,
  ChevronLeftIcon,
} from '../icons';

// Utility function to fetch and filter tasks by project
const fetchProjectTasks = async (
  projectId: number | string,
): Promise<Task[]> => {
  try {
    const response = await tasksAPI.getAll({ 'project.id': Number(projectId) });
    const allTasks = response.data.tasks || [];
    return allTasks.filter(
      (task: Task) => task.project?.id === Number(projectId),
    );
  } catch (error) {
    console.error('Failed to fetch tasks for project:', error);
    return [];
  }
};

// Premium Icons
const Icons = {
  plus: <PlusIcon className="w-5 h-5" strokeWidth={2} />,
  search: <SearchIcon className="w-5 h-5" strokeWidth={1.5} />,
  folder: <FolderIcon className="w-5 h-5" strokeWidth={1.5} />,
  check: <CheckIcon className="w-4 h-4" strokeWidth={2} />,
  eye: <EyeIcon className="w-4 h-4" strokeWidth={1.5} />,
  edit: <EditIcon className="w-4 h-4" strokeWidth={1.5} />,
  trash: <TrashIcon className="w-4 h-4" strokeWidth={1.5} />,
  chevronLeft: <ChevronLeftIcon className="w-5 h-5" strokeWidth={1.5} />,
};

const ProjectList: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // Horizontal drag scroll
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!tableContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - tableContainerRef.current.offsetLeft);
    setScrollLeft(tableContainerRef.current.scrollLeft);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !tableContainerRef.current) return;
      e.preventDefault();
      const x = e.pageX - tableContainerRef.current.offsetLeft;
      const walk = (x - startX) * 1.5;
      tableContainerRef.current.scrollLeft = scrollLeft - walk;
    },
    [isDragging, startX, scrollLeft],
  );

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: t('projects.deleteTitle'),
      message: t('projects.deleteConfirm'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await projectsAPI.delete(id);
      setProjects(projects.filter((p) => p.id !== id));
    } catch (error) {
      console.error('Failed to delete project:', error);
      showToast(t('projects.deleteFailed'), 'error');
    }
  };

  const filteredProjects = projects.filter(
    (project) =>
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.description &&
        project.description.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  const getStatusColor = (status: string) => {
    if (isDark) {
      switch (status) {
        case 'active':
          return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
        case 'completed':
          return 'bg-violet-500/20 text-violet-400 border border-violet-500/30';
        case 'on_hold':
          return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
        default:
          return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      }
    }
    switch (status) {
      case 'active':
        return 'bg-emerald-100 text-emerald-700';
      case 'completed':
        return 'bg-violet-100 text-violet-700';
      case 'on_hold':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm'
    : 'bg-white border border-slate-200 shadow-sm';

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1
            className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('projects.title')}
          </h1>
          <p
            className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
          >
            {filteredProjects.length} projects total
          </p>
        </div>
        <Link
          to="/manager/projects/new"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
        >
          {Icons.plus}
          <span>{t('projects.newProject')}</span>
        </Link>
      </div>

      {/* Search */}
      <div className={`${cardClass} rounded-xl p-4`}>
        <div className="relative">
          <span
            className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
          >
            {Icons.search}
          </span>
          <input
            type="text"
            placeholder={t('projects.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2.5 rounded-lg text-sm transition-colors ${
              isDark
                ? 'bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 focus:border-violet-500 focus:ring-1 focus:ring-violet-500'
                : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-violet-500 focus:ring-1 focus:ring-violet-500'
            }`}
          />
        </div>
      </div>

      {/* Projects Table */}
      <div className={`${cardClass} rounded-2xl overflow-hidden`}>
        {filteredProjects.length === 0 ? (
          <div className="p-12 text-center">
            <div
              className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${isDark ? 'bg-slate-900' : 'bg-slate-100'}`}
            >
              <span className={isDark ? 'text-slate-600' : 'text-slate-400'}>
                {Icons.folder}
              </span>
            </div>
            <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              {t('projects.noProjects')}
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Card Layout */}
            <div className="md:hidden divide-y divide-slate-700/50">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className={`p-4 ${isDark ? 'hover:bg-slate-900/50' : 'hover:bg-slate-50'}`}
                >
                  {/* Project Name & Status */}
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {project.name}
                      </h3>
                      {project.description && (
                        <p
                          className={`text-xs mt-0.5 truncate ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                        >
                          {project.description}
                        </p>
                      )}
                    </div>
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-lg flex-shrink-0 ${getStatusColor(project.status)}`}
                    >
                      {project.status}
                    </span>
                  </div>

                  {/* Client */}
                  <div className="mb-2">
                    {project.is_internal ? (
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-lg ${isDark ? 'bg-slate-700/50 text-slate-400' : 'bg-slate-100 text-slate-600'}`}
                      >
                        {t('projects.internal')}
                      </span>
                    ) : (
                      <span
                        className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                      >
                        {project.client?.name || '-'}
                      </span>
                    )}
                  </div>

                  {/* Badges Row */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {project.sred_eligible && (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-500">
                        {Icons.check} SR&ED
                      </span>
                    )}
                    {project.cdae_eligible && (
                      <span className="inline-flex items-center gap-1 text-xs text-violet-500">
                        {Icons.check} CDAE
                      </span>
                    )}
                    {project.start_date && (
                      <span
                        className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                      >
                        {new Date(project.start_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <Link
                      to={`/manager/projects/${project.id}`}
                      className={`p-2 rounded-lg transition-all ${isDark ? 'hover:bg-slate-700 text-cyan-400' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                      title={t('common.view')}
                    >
                      {Icons.eye}
                    </Link>
                    <Link
                      to={`/manager/projects/${project.id}/edit`}
                      className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                      title={t('common.edit')}
                    >
                      {Icons.edit}
                    </Link>
                    <button
                      onClick={() => handleDelete(project.id)}
                      className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                      title={t('common.delete')}
                    >
                      {Icons.trash}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div
              ref={tableContainerRef}
              className={`hidden md:block overflow-auto max-h-[calc(100vh-450px)] cursor-grab select-none ${isDragging ? 'cursor-grabbing' : ''}`}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <table className="w-full relative">
                <thead
                  className={`sticky top-0 z-10 ${isDark ? 'bg-slate-900/80' : 'bg-slate-50'}`}
                >
                  <tr>
                    <th
                      className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {t('projects.projectName')}
                    </th>
                    <th
                      className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {t('projects.client')}
                    </th>
                    <th
                      className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {t('common.status')}
                    </th>
                    <th
                      className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      SR&ED
                    </th>
                    <th
                      className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      CDAE
                    </th>
                    <th
                      className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {t('projects.startDate')}
                    </th>
                    <th
                      className={`px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`divide-y ${isDark ? 'divide-slate-700/50' : 'divide-slate-100'}`}
                >
                  {filteredProjects.map((project) => (
                    <tr
                      key={project.id}
                      className={`transition-colors ${isDark ? 'hover:bg-slate-900/50' : 'hover:bg-slate-50'}`}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div
                            className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                          >
                            {project.name}
                          </div>
                          {project.description && (
                            <div
                              className={`text-sm truncate max-w-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                            >
                              {project.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {project.is_internal ? (
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-lg ${isDark ? 'bg-slate-700/50 text-slate-400' : 'bg-slate-100 text-slate-600'}`}
                          >
                            {t('projects.internal')}
                          </span>
                        ) : (
                          <span
                            className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                          >
                            {project.client?.name || '-'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg ${getStatusColor(project.status)}`}
                        >
                          {project.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {project.sred_eligible ? (
                          <span className="inline-flex items-center gap-1 text-emerald-500">
                            {Icons.check} {t('projects.eligible')}
                          </span>
                        ) : (
                          <span
                            className={
                              isDark ? 'text-slate-600' : 'text-slate-400'
                            }
                          >
                            {t('projects.notEligible')}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {project.cdae_eligible ? (
                          <span className="inline-flex items-center gap-1 text-violet-500">
                            {Icons.check} {t('projects.eligible')}
                          </span>
                        ) : (
                          <span
                            className={
                              isDark ? 'text-slate-600' : 'text-slate-400'
                            }
                          >
                            {t('projects.notEligible')}
                          </span>
                        )}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                      >
                        {project.start_date
                          ? new Date(project.start_date).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/manager/projects/${project.id}`}
                            className={`p-2 rounded-lg transition-all ${isDark ? 'hover:bg-slate-700 text-cyan-400' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                            title={t('common.view')}
                          >
                            {Icons.eye}
                          </Link>
                          <Link
                            to={`/manager/projects/${project.id}/edit`}
                            className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                            title={t('common.edit')}
                          >
                            {Icons.edit}
                          </Link>
                          <button
                            onClick={() => handleDelete(project.id)}
                            className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                            title={t('common.delete')}
                          >
                            {Icons.trash}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ProjectForm: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { id: projectId } = useParams<{ id: string }>();
  const [project, setProject] = useState<Partial<Project>>({
    name: '',
    description: '',
    status: 'active',
    sred_eligible: false,
    cdae_eligible: false,
    start_date: '',
    end_date: '',
    client_id: undefined,
    is_internal: false,
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const isEdit = !!projectId;

  useEffect(() => {
    fetchClients();
    specialtiesAPI
      .getAll()
      .then((r) => setSpecialties(r.data || []))
      .catch(() => setSpecialties([]));
    usersAPI
      .getAll()
      .then((r) => setUsers((r.data.users || []).filter((u: User) => u.active)))
      .catch(() => setUsers([]));
    if (projectId) {
      fetchProject();
      fetchAndSetTasks();
    }
  }, [projectId]);

  // Active users who hold a given specialty (for the per-techno suggestion view).
  const usersForSpecialty = (specialtyId: number) =>
    users.filter((u) => (u.specialty_ids || []).includes(specialtyId));

  const toggleSpecialty = (specialtyId: number) => {
    setProject((prev) => {
      const current = prev.specialty_ids || [];
      return {
        ...prev,
        specialty_ids: current.includes(specialtyId)
          ? current.filter((x) => x !== specialtyId)
          : [...current, specialtyId],
      };
    });
  };

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data.clients || response.data || []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchProject = async () => {
    try {
      const response = await projectsAPI.getById(Number(projectId));
      const projectData = response.data.project;
      setProject({
        ...projectData,
        sred_eligible: projectData.sred_eligible || false,
        cdae_eligible: projectData.cdae_eligible || false,
        is_internal: projectData.is_internal || false,
        client_id: projectData.client_id || undefined,
      });
    } catch (error) {
      console.error('Failed to fetch project:', error);
      setError(t('projects.loadFailed'));
    }
  };

  const fetchAndSetTasks = async () => {
    if (projectId) {
      const projectTasks = await fetchProjectTasks(projectId);
      setTasks(projectTasks);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEdit) {
        await projectsAPI.update(Number(projectId), project);
      } else {
        await projectsAPI.create(project as Omit<Project, 'id'>);
      }
      navigate('/manager/projects');
    } catch (error: any) {
      setError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          t('projects.saveFailed'),
      );
    } finally {
      setLoading(false);
    }
  };

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm rounded-2xl'
    : 'bg-white border border-slate-200 shadow-sm rounded-2xl';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const labelClass = `block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`;

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/manager/projects')}
            className={`p-2 rounded-xl transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
          >
            {Icons.chevronLeft}
          </button>
          <h1
            className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {isEdit ? t('projects.editProject') : t('projects.createProject')}
          </h1>
        </div>

        {error && (
          <div className="p-4 bg-rose-500/10 border border-rose-500/30 text-rose-400 rounded-xl text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className={`${cardClass} p-6 space-y-5`}>
            <div>
              <label className={labelClass}>
                {t('projects.projectName')} *
              </label>
              <input
                type="text"
                required
                value={project.name || ''}
                onChange={(e) =>
                  setProject({ ...project, name: e.target.value })
                }
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>{t('common.description')}</label>
              <textarea
                rows={4}
                value={project.description || ''}
                onChange={(e) =>
                  setProject({ ...project, description: e.target.value })
                }
                className={inputClass}
              />
            </div>

            {/* Client Assignment */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_internal"
                  checked={project.is_internal || false}
                  onChange={(e) => {
                    const isInternal = e.target.checked;
                    setProject({
                      ...project,
                      is_internal: isInternal,
                      client_id: isInternal ? undefined : project.client_id,
                    });
                  }}
                  disabled={isEdit}
                  className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-slate-600 rounded bg-slate-900"
                />
                <label
                  htmlFor="is_internal"
                  className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'} ${isEdit ? 'opacity-50' : ''}`}
                >
                  {t('projects.internalProject')}{' '}
                  {isEdit ? t('projects.cannotBeChanged') : ''}
                </label>
              </div>

              {!project.is_internal && (
                <div>
                  <label className={labelClass}>
                    {t('projects.client')} *{' '}
                    {isEdit ? t('projects.cannotBeChanged') : ''}
                  </label>
                  <select
                    value={project.client_id || ''}
                    onChange={(e) =>
                      setProject({
                        ...project,
                        client_id: e.target.value
                          ? Number(e.target.value)
                          : undefined,
                      })
                    }
                    disabled={isEdit}
                    className={`${inputClass} ${isEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                    required={!project.is_internal && !isEdit}
                  >
                    <option value="">{t('projects.selectClient')}</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                        {client.company ? ` (${client.company})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>{t('common.status')}</label>
                <select
                  value={project.status || 'active'}
                  onChange={(e) =>
                    setProject({ ...project, status: e.target.value })
                  }
                  className={inputClass}
                >
                  <option value="active">{t('projects.active')}</option>
                  <option value="on_hold">{t('projects.onHold')}</option>
                  <option value="completed">{t('projects.completed')}</option>
                </select>
              </div>

              <div className="space-y-4 flex flex-col justify-center">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="sred_eligible"
                    checked={project.sred_eligible || false}
                    onChange={(e) =>
                      setProject({
                        ...project,
                        sred_eligible: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-slate-600 rounded bg-slate-900"
                  />
                  <label
                    htmlFor="sred_eligible"
                    className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                  >
                    {t('projects.sredEligible')}
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="cdae_eligible"
                    checked={project.cdae_eligible || false}
                    onChange={(e) =>
                      setProject({
                        ...project,
                        cdae_eligible: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-slate-600 rounded bg-slate-900"
                  />
                  <label
                    htmlFor="cdae_eligible"
                    className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                  >
                    {t('projects.cdaeEligible')}
                  </label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>{t('projects.startDate')}</label>
                <input
                  type="date"
                  value={project.start_date || ''}
                  onChange={(e) =>
                    setProject({ ...project, start_date: e.target.value })
                  }
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>{t('projects.endDate')}</label>
                <input
                  type="date"
                  value={project.end_date || ''}
                  onChange={(e) =>
                    setProject({ ...project, end_date: e.target.value })
                  }
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>
                {t('projects.technologies', {
                  defaultValue: 'Technologies / skills',
                })}
              </label>
              {specialties.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {specialties.map((spec) => {
                    const selected = (project.specialty_ids || []).includes(spec.id);
                    return (
                      <button
                        key={spec.id}
                        type="button"
                        onClick={() => toggleSpecialty(spec.id)}
                        className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                          selected
                            ? 'bg-violet-500/20 border-violet-400 text-violet-200'
                            : isDark
                              ? 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-500'
                              : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'
                        }`}
                      >
                        {spec.name}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p
                  className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                >
                  {t('users.noSkills', {
                    defaultValue: 'No skills yet — add some in Admin → Skills.',
                  })}
                </p>
              )}
            </div>

            {/* Live, per-technology employee suggestions (updates as you toggle) */}
            {(project.specialty_ids || []).length > 0 && (
              <div>
                <label className={labelClass}>
                  {t('projects.suggestedTeam', {
                    defaultValue: 'Suggested team',
                  })}
                </label>
                <div className="space-y-3">
                  {(project.specialty_ids || []).map((sid) => {
                    const spec = specialties.find((s) => s.id === sid);
                    if (!spec) return null;
                    const matches = usersForSpecialty(sid);
                    return (
                      <div
                        key={sid}
                        className={`p-3 rounded-lg border ${isDark ? 'bg-slate-900/30 border-slate-700/50' : 'bg-slate-50 border-slate-200'}`}
                      >
                        <div
                          className={`text-sm font-medium mb-1 ${isDark ? 'text-slate-200' : 'text-slate-800'}`}
                        >
                          {spec.name}
                        </div>
                        {matches.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {matches.map((u) => (
                              <span
                                key={u.id}
                                className={`px-2 py-0.5 text-xs rounded-full ${isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-700'}`}
                              >
                                {u.full_name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span
                            className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                          >
                            {t('projects.noOneForSpec', {
                              defaultValue: 'No one has this skill yet.',
                            })}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className={`flex justify-end gap-3 pt-2`}>
            <button
              type="button"
              onClick={() => navigate('/manager/projects')}
              className={`px-5 py-2.5 rounded-xl font-medium transition-colors ${
                isDark
                  ? 'bg-slate-900 text-slate-300 hover:bg-slate-700 border border-slate-700'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50"
            >
              {loading
                ? t('common.saving')
                : isEdit
                  ? t('projects.updateProject')
                  : t('projects.createProject')}
            </button>
          </div>
        </form>

        {/* Project Tasks Section - Only show when editing */}
        {isEdit && projectId && (
          <div className={`${cardClass} p-6`}>
            <h2
              className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('common.tasks')}
            </h2>
            {tasks.length > 0 ? (
              <div className="space-y-3">
                {tasks.map((task) => (
                  <div
                    key={task.id}
                    className={`p-4 rounded-lg border ${isDark ? 'bg-slate-900/30 border-slate-700/50' : 'bg-slate-50 border-slate-200'}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/manager/tasks/${task.id}`}
                          className={`font-medium truncate block hover:underline ${isDark ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}
                        >
                          {task.title}
                        </Link>
                        {task.description && (
                          <p
                            className={`text-sm mt-1 line-clamp-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                          >
                            {task.description}
                          </p>
                        )}
                      </div>
                      <span
                        className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg whitespace-nowrap ${
                          task.status === 'completed'
                            ? isDark
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : 'bg-emerald-100 text-emerald-700'
                            : task.status === 'in_progress'
                              ? isDark
                                ? 'bg-blue-500/20 text-blue-400'
                                : 'bg-blue-100 text-blue-700'
                              : isDark
                                ? 'bg-slate-700/50 text-slate-300'
                                : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {t(`tasks.status.${task.status}`)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p
                className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('common.noData')}
              </p>
            )}
          </div>
        )}

        {/* External Integrations Section - Only show when editing */}
        {isEdit && projectId && (
          <div className={`${cardClass} p-6`}>
            <h2
              className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('integrations.title')}
            </h2>
            <p
              className={`text-sm mb-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
            >
              {t('integrations.subtitle')}
            </p>
            <ProjectIntegrations
              projectId={Number(projectId)}
              onUpdate={fetchProject}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const ProjectDetail: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [suggestions, setSuggestions] = useState<SuggestedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (projectId) {
      fetchProject();
      fetchAndSetTasks();
      projectsAPI
        .suggestedUsers(Number(projectId))
        .then((r) => setSuggestions(r.data.suggested_users || []))
        .catch(() => setSuggestions([]));
    }
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await projectsAPI.getById(Number(projectId));
      setProject(response.data.project);
    } catch (error) {
      console.error('Failed to fetch project:', error);
      setError(t('projects.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAndSetTasks = async () => {
    if (projectId) {
      const projectTasks = await fetchProjectTasks(projectId);
      setTasks(projectTasks);
    }
  };

  const cardClass = isDark
    ? 'bg-slate-900 border border-slate-700 rounded-2xl'
    : 'bg-white border border-slate-200 shadow-sm rounded-2xl';

  const getStatusColor = (status: string) => {
    if (isDark) {
      switch (status) {
        case 'active':
          return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
        case 'completed':
          return 'bg-violet-500/20 text-violet-400 border border-violet-500/30';
        case 'on_hold':
          return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
        default:
          return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      }
    }
    switch (status) {
      case 'active':
        return 'bg-emerald-100 text-emerald-700';
      case 'completed':
        return 'bg-violet-100 text-violet-700';
      case 'on_hold':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="p-6">
        <div
          className={`p-4 rounded-xl ${isDark ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'}`}
        >
          {error || t('projects.loadFailed')}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className=" mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/manager/projects')}
              className={`p-2 rounded-xl transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
            >
              {Icons.chevronLeft}
            </button>
            <div>
              <h1
                className={`text-xl sm:text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {project.name}
              </h1>
              <div className="flex items-center gap-2 sm:gap-3 mt-1">
                <span
                  className={`inline-flex px-2 sm:px-2.5 py-1 text-xs font-semibold rounded-lg ${getStatusColor(project.status)}`}
                >
                  {project.status}
                </span>
                {project.is_internal && (
                  <span
                    className={`inline-flex px-2 sm:px-2.5 py-1 text-xs font-medium rounded-lg ${isDark ? 'bg-slate-700/50 text-slate-400' : 'bg-slate-100 text-slate-600'}`}
                  >
                    {t('projects.internal')}
                  </span>
                )}
              </div>
            </div>
          </div>
          <Link
            to={`/manager/projects/${project.id}/edit`}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
          >
            {Icons.edit}
            <span>{t('common.edit')}</span>
          </Link>
        </div>

        {/* Project Details */}
        <div className={`${cardClass} p-6`}>
          <h2
            className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('projects.details')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {project.description && (
              <div className="md:col-span-2">
                <label
                  className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                >
                  {t('common.description')}
                </label>
                <p className={isDark ? 'text-slate-200' : 'text-slate-700'}>
                  {project.description}
                </p>
              </div>
            )}

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('projects.client')}
              </label>
              <p className={isDark ? 'text-slate-200' : 'text-slate-700'}>
                {project.is_internal
                  ? t('projects.internal')
                  : project.client?.name || '-'}
              </p>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('common.status')}
              </label>
              <span
                className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg ${getStatusColor(project.status)}`}
              >
                {project.status}
              </span>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                SR&ED {t('projects.eligible')}
              </label>
              <p className={isDark ? 'text-slate-200' : 'text-slate-700'}>
                {project.sred_eligible ? (
                  <span className="inline-flex items-center gap-1 text-emerald-500">
                    {Icons.check} {t('common.yes')}
                  </span>
                ) : (
                  <span
                    className={isDark ? 'text-slate-500' : 'text-slate-400'}
                  >
                    {t('common.no')}
                  </span>
                )}
              </p>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                CDAE {t('projects.eligible')}
              </label>
              <p className={isDark ? 'text-slate-200' : 'text-slate-700'}>
                {project.cdae_eligible ? (
                  <span className="inline-flex items-center gap-1 text-violet-500">
                    {Icons.check} {t('common.yes')}
                  </span>
                ) : (
                  <span
                    className={isDark ? 'text-slate-500' : 'text-slate-400'}
                  >
                    {t('common.no')}
                  </span>
                )}
              </p>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('projects.startDate')}
              </label>
              <p className={isDark ? 'text-slate-200' : 'text-slate-700'}>
                {project.start_date
                  ? new Date(project.start_date).toLocaleDateString()
                  : '-'}
              </p>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('projects.endDate')}
              </label>
              <p className={isDark ? 'text-slate-200' : 'text-slate-700'}>
                {project.end_date
                  ? new Date(project.end_date).toLocaleDateString()
                  : '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Technologies & suggested team */}
        <div className={`${cardClass} p-6`}>
          <h2
            className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('projects.technologies', {
              defaultValue: 'Technologies / skills',
            })}
          </h2>
          {project.specialties && project.specialties.length > 0 ? (
            <div className="flex flex-wrap gap-2 mb-6">
              {project.specialties.map((s) => (
                <span
                  key={s.id}
                  className={`px-3 py-1 text-sm rounded-full ${isDark ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700'}`}
                >
                  {s.name}
                </span>
              ))}
            </div>
          ) : (
            <p
              className={`text-sm mb-6 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
            >
              {t('projects.noTechnologies', {
                defaultValue: 'No technologies set for this project.',
              })}
            </p>
          )}

          <h3
            className={`text-base font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('projects.suggestedTeam', { defaultValue: 'Suggested team' })}
          </h3>
          {suggestions.length > 0 ? (
            <ul className="space-y-2">
              {suggestions.map((u) => (
                <li
                  key={u.id}
                  className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${isDark ? 'bg-slate-900/30 border-slate-700/50' : 'bg-slate-50 border-slate-200'}`}
                >
                  <div className="min-w-0">
                    <span
                      className={`font-medium ${isDark ? 'text-slate-200' : 'text-slate-800'}`}
                    >
                      {u.full_name}
                    </span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {u.matched_specialties.map((s) => (
                        <span
                          key={s.id}
                          className={`px-2 py-0.5 text-xs rounded-full ${isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-700'}`}
                        >
                          {s.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span
                    className={`text-sm font-semibold whitespace-nowrap ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {t('projects.matchCount', {
                      count: u.match_count,
                      defaultValue: '{{count}} match',
                    })}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p
              className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
            >
              {t('projects.noSuggestions', {
                defaultValue:
                  'No matching members — set technologies here and skills on users.',
              })}
            </p>
          )}
        </div>

        {/* Project Tasks Section */}
        <div className={`${cardClass} p-6`}>
          <h2
            className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('common.tasks')}
          </h2>
          {tasks.length > 0 ? (
            <div className="space-y-3">
              {tasks.map((task) => (
                <div
                  key={task.id}
                  className={`p-4 rounded-lg border ${isDark ? 'bg-slate-900/30 border-slate-700/50' : 'bg-slate-50 border-slate-200'}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/manager/tasks/${task.id}`}
                        className={`font-medium truncate block hover:underline ${isDark ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}
                      >
                        {task.title}
                      </Link>
                      {task.description && (
                        <p
                          className={`text-sm mt-1 line-clamp-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                        >
                          {task.description}
                        </p>
                      )}
                    </div>
                    <span
                      className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg whitespace-nowrap ${
                        task.status === 'completed'
                          ? isDark
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'bg-emerald-100 text-emerald-700'
                          : task.status === 'in_progress'
                            ? isDark
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-blue-100 text-blue-700'
                            : isDark
                              ? 'bg-slate-700/50 text-slate-300'
                              : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {t(`tasks.status.${task.status}`)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p
              className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
            >
              {t('common.noData')}
            </p>
          )}
        </div>

        {/* Integrations */}
        {projectId && (
          <div className={`${cardClass} p-6`}>
            <h2
              className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('integrations.title')}
            </h2>
            <p
              className={`text-sm mb-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
            >
              {t('integrations.subtitle')}
            </p>
            <ProjectIntegrations
              projectId={Number(projectId)}
              onUpdate={fetchProject}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const ProjectManagement: React.FC = () => {
  return (
    <Routes>
      <Route index element={<ProjectList />} />
      <Route path="new" element={<ProjectForm />} />
      <Route path=":id" element={<ProjectDetail />} />
      <Route path=":id/edit" element={<ProjectForm />} />
    </Routes>
  );
};

export default ProjectManagement;
